import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Paper,
  Autocomplete,
  CircularProgress,
  TextField,
  IconButton,
  Grid,
  Box,
  MenuItem,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RecentOrders from "./RecentOrders"; // Import the RecentOrders component
import CarDetails from "./CarDetails";
import SubscriptionDetails from "./SubscriptionDetails";
import PaymentsList from "./PaymentsList";

function Main() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [edit, setEdit] = useState(false);
  const [editFields, setEditFields] = useState({
    name: "",
    phone: "",
    email: "",
    notes: "",
    address: "",
  });
  const [carFields, setCarFields] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [editTxnIdx, setEditTxnIdx] = useState(null);
  const [editTxnValue, setEditTxnValue] = useState("");
  const [editTxnOpen, setEditTxnOpen] = useState(false);
  const [editPriceIdx, setEditPriceIdx] = useState(null);
  const [editPriceValue, setEditPriceValue] = useState("");
  const [editPriceOpen, setEditPriceOpen] = useState(false);

  const [editWasherIdx, setEditWasherIdx] = useState(null);
  const [washerLoading, setWasherLoading] = useState(false);
  const [editWasherOptions, setEditWasherOptions] = useState([]);
  const [editWasher, setEditWasher] = useState(null);
  const [editWasherQuery, setEditWasherQuery] = useState("");
  const editWasherDebounceRef = useRef(); // <-- define the ref here

  useEffect(() => {
    if (editWasherIdx) {
      fetchWashers();
    }
    // eslint-disable-next-line
  }, [editWasherIdx]);

  const fetchWashers = (search) => {
    setWasherLoading(true);
    const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
    fetch(
      `${process.env.REACT_APP_SERVER_URI}/washers/options?search=${encodeURIComponent(search)}`,
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    )
      .then((res) => res.json())
      .then((result) => {
        setEditWasherOptions(result || []);
        setWasherLoading(false);
      })
      .catch(() => {
        setEditWasherOptions([]);
        setWasherLoading(false);
      });
  };

  const fetchEditWasherOptions = (search = "") => {
    const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
    fetch(
      `${process.env.REACT_APP_SERVER_URI}/washers/options?search=${encodeURIComponent(search)}`,
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    )
      .then((res) => res.json())
      .then((result) => {
        setEditWasherOptions(result || []);
      })
      .catch(() => {
        setEditWasherOptions([]);
      });
  };

  const handleEditWasherInputChange = (event, value) => {
    setEditWasherQuery(value);
    if (editWasherDebounceRef.current)
      clearTimeout(editWasherDebounceRef.current);
    editWasherDebounceRef.current = setTimeout(() => {
      fetchEditWasherOptions(value);
    }, 400);
  };

  const updateWashField = async (subscriptionId, updateObj, onSuccess) => {
    const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URI}/subscriptions/washer/update/${subscriptionId}`,
        {
          method: "POST",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateObj),
        }
      );

      const data = await res.json();
      console.log(data);

      if (!res.ok) {
        throw new Error(data?.error || "Update failed");
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      alert("Failed to update washer.");
      console.error(err);
    }
  };

  const handleEditWasherSave = () => {
    updateWashField(
      editWasherIdx,
      {
        washerId: editWasher._id,
        washerName: editWasher.name,
      },
      () => {
        setEditWasherIdx(null);
        setEditWasher(null);
        setEditWasherQuery("");
        setEditWasherOptions([]);
      }
    );
  };

  useEffect(() => {
    if (!userId) return;
    const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/users/userDetails/${userId}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setUser(data);
        setEditFields({
          name: data.name || "",
          phone: data.phone || "",
          email: data.email || "",
          notes: data.notes || "",
          address: data.address || "",
        });
      })
      .catch(() => setUser(null));
  }, [userId]);

  // Fetch cars for the user
  useEffect(() => {
    if (!userId) return;
    const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/cars/user/${userId}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setCarFields(
          Array.isArray(data) ? data.map((car) => ({ ...car })) : []
        );
      })
      .catch(() => setCarFields([]));
  }, [userId]);

  const handleFieldChange = (e) => {
    setEditFields({ ...editFields, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
    console.log(editFields)
    fetch(`${process.env.REACT_APP_SERVER_URI}/users/update/${userId}`, {
      method: "Post",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify(editFields),
    })
      .then((res) => res.json())
      .then((data) => {
        if(!data.error){
          setUser(v=>({...v, ...editFields}));
          setEdit(false);
        }
      });
  };

  const handleTxnEditSave = async () => {
    const idx = editTxnIdx;
    const sub = subscriptions[idx];
    setSubscriptions((subs) =>
      subs.map((s, i) =>
        i === idx ? { ...s, transactionId: editTxnValue } : s
      )
    );
    setEditTxnOpen(false);
    setEditTxnIdx(null);
    try {
      const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
      await fetch(`${process.env.REACT_APP_SERVER_URI}/subscriptions/update/${sub._id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ transactionId: editTxnValue }),
      });
    } catch (err) {
      alert("Failed to update transaction ID");
    }
    setEditTxnValue("");
  };

  const handlePriceEditSave = async () => {
    const idx = editPriceIdx;
    const sub = subscriptions[idx];
    setSubscriptions((subs) =>
      subs.map((s, i) => (i === idx ? { ...s, price: editPriceValue } : s))
    );
    setEditPriceOpen(false);
    setEditPriceIdx(null);
    try {
      const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
      await fetch(`${process.env.REACT_APP_SERVER_URI}/subscriptions/update/${sub._id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ price: editPriceValue }),
      });
    } catch (err) {
      alert("Failed to update price");
    }
    setEditPriceValue("");
  };

  return (
    <div>
      <Paper sx={{ p: 2, display: "flex", flexDirection: "column", mb: 2 }} variant="outlined">
        {user ? (
          <>
            <div
              style={{ display: "flex", alignItems: "center", width: "100%" }}
            >
              <span
                style={{
                  marginRight: edit ? 16 : 32,
                  padding: edit ? 0 : "0 8px",
                }}
              >
                {edit ? (
                  <TextField
                    name="name"
                    value={editFields.name}
                    onChange={handleFieldChange}
                    size="small"
                    label="Name"
                    sx={{ width: 120 }}
                  />
                ) : (
                  <span style={{ display: "inline-block", minWidth: 120 }}>
                    <strong>Name:</strong>{" "}
                    <span style={{ paddingLeft: 4 }}>{user.name}</span>
                  </span>
                )}
              </span>
              <span
                style={{
                  marginRight: edit ? 16 : 32,
                  padding: edit ? 0 : "0 8px",
                }}
              >
                {edit ? (
                  <TextField
                  name="phone"
                  value={editFields.phone}
                    onChange={handleFieldChange}
                    size="small"
                    label="Phone"
                    sx={{ width: 120 }}
                  />
                ) : (
                  <span style={{ display: "inline-block", minWidth: 120 }}>
                    <strong>Phone:</strong>{" "}
                    <span style={{ paddingLeft: 4 }}>{user.phone}</span>
                  </span>
                )}
              </span>
              <span
                style={{
                  marginRight: edit ? 16 : 32,
                  padding: edit ? 0 : "0 8px",
                }}
              >
                {edit ? (
                  <TextField
                    name="email"
                    value={editFields.email}
                    onChange={handleFieldChange}
                    size="small"
                    label="Email"
                    sx={{ width: 180 }}
                  />
                ) : (
                  <span style={{ display: "inline-block", minWidth: 180 }}>
                    <strong>Email:</strong>{" "}
                    <span style={{ paddingLeft: 4 }}>{user.email}</span>
                  </span>
                )}
              </span>
              <span
                style={{
                  marginRight: edit ? 16 : 32,
                  padding: edit ? 0 : "0 8px",
                }}
              >
                {edit ? (
                  <TextField
                    name="address"
                    value={editFields.address}
                    onChange={handleFieldChange}
                    size="small"
                    label="Address"
                    sx={{ width: "26rem" }}
                  />
                ) : (
                  <span style={{ display: "inline-block", minWidth: 280 }}>
                    <strong>Address:</strong>{" "}
                    <span style={{ paddingLeft: 4 }}>{user.address}</span>
                  </span>
                )}
              </span>
              <span style={{ flex: 1 }} />
              {edit ? (
                <>
                  <IconButton color="primary" onClick={handleSave}>
                    <SaveIcon />
                  </IconButton>
                  <IconButton color="secondary" onClick={() => setEdit(false)}>
                    <VisibilityIcon />
                  </IconButton>
                </>
              ) : (
                <IconButton color="primary" onClick={() => setEdit(true)}>
                  <EditIcon />
                </IconButton>
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                marginTop: 12,
              }}
            >
              <span
                style={{
                  marginRight: edit ? 16 : 32,
                  padding: edit ? 0 : "0 8px",
                  width: "100%",
                }}
              >
                {edit ? (
                  <TextField
                  name="notes"
                  value={editFields.notes}
                  onChange={handleFieldChange}
                    size="small"
                    label="Notes"
                    sx={{ width: "100%" }}
                    multiline
                  />
                ) : (
                  <span style={{ display: "inline-block", minWidth: 180 }}>
                    <strong>Notes:</strong>{" "}
                    <span style={{ paddingLeft: 4 }}>{user.notes}</span>
                  </span>
                )}
              </span>
            </div>
            {user?.imageUrl && (
              <div style={{ margin: "16px 0" }}>
                <img
                  src={user.imageUrl}
                  alt="User"
                  style={{ maxWidth: 200, maxHeight: 120 }}
                />
              </div>
            )}
          </>
        ) : (
          <span>Loading user details...</span>
        )}
      </Paper>
        {Array.isArray(carFields) && carFields.length > 0 ? (
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column", mb: 2,  height: "100%", width:'98%'}} variant="outlined">
              <Grid container spacing={2} direction='row'>
              {carFields.map((car, idx) => (
                <React.Fragment key={car._id || idx}>
                  <CarDetails idx={idx} car={car} carFields={carFields} setCarFields={setCarFields} />
                  <SubscriptionDetails carId={car._id} userId={userId} />
                  <PaymentsList userId={userId} carId={car._id} />
                  <RecentOrders userId={userId} isPending={true} carId={car._id} />
                  <RecentOrders userId={userId} carId={car._id} />
                  <RecentOrders userId={userId} carId={car._id} isdescrepancies={true} />
                </React.Fragment>
              ))}
          </Grid>
            </Paper>
        ) : (
          <span>No cars found.</span>
        )}
      <Dialog open={editTxnOpen} onClose={() => setEditTxnOpen(false)}>
        <DialogTitle>Edit Transaction ID</DialogTitle>
        <DialogContent>
          <TextField
            label="Transaction ID"
            value={editTxnValue}
            onChange={(e) => setEditTxnValue(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTxnOpen(false)}>Cancel</Button>
          <Button
            onClick={handleTxnEditSave}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editPriceOpen} onClose={() => setEditPriceOpen(false)}>
        <DialogTitle>Edit Price</DialogTitle>
        <DialogContent>
          <TextField
            label="Price"
            type="number"
            value={editPriceValue}
            onChange={(e) => setEditPriceValue(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPriceOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePriceEditSave}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(editWasherIdx)}
        onClose={() => setEditWasherIdx(false)}
      >
        <DialogTitle>Change Washer</DialogTitle>
        <Box px={3} py={2}>
          <Autocomplete
            options={editWasherOptions}
            getOptionLabel={(option) => option.name || ""}
            value={editWasher}
            onChange={(event, value) => setEditWasher(value)}
            inputValue={editWasherQuery}
            onInputChange={handleEditWasherInputChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Washer"
                margin="normal"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {washerLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button onClick={() => setEditWasherIdx(false)} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleEditWasherSave}
              variant="contained"
              color="primary"
              disabled={!editWasher}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>
    </div>
  );
}

export default Main;
